import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useI18n } from '../../i18n';
import emailService from '../../services/emailService';
import { useEmail } from '../../context/EmailContext';
import { notify } from '../CustomToast';
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaTimes } from 'react-icons/fa';
import '../Projects/ProjectComposer.css';

const AccountSetupModal = ({ isOpen, onClose }) => {
    const { t } = useI18n();
    const { fetchAccounts, attachAccount } = useEmail();

    const [step, setStep] = useState(1);
    const [isTesting, setIsTesting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        email: '',
        display_name: '',
        imap_host: '',
        imap_port: 993,
        imap_encryption: 'ssl',
        imap_username: '',
        imap_password: '',
        smtp_host: '',
        smtp_port: 587,
        smtp_encryption: 'tls',
        smtp_username: '',
        smtp_password: '',
        is_default: false,
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        // Clear error for this field when user edits it
        if (errors[name]) {
            setErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
        }
    };

    const handleTestConnection = async () => {
        setIsTesting(true);
        setTestResult(null);
        setErrors({});

        try {
            const response = await emailService.testConnection({
                ...formData,
                imap_username: formData.imap_username || formData.email,
                smtp_username: formData.smtp_username || formData.email,
                smtp_password: formData.smtp_password || formData.imap_password,
            });

            setTestResult(response.data);

            if (response.data.imap && response.data.smtp) {
                notify('success', 'Connection test successful!');
                setStep(2);
            } else {
                notify('error', 'Connection test failed. Please check your settings.');
            }
        } catch (error) {
            if (error?.status === 422) {
                setErrors(error.errors || {});
            } else {
                setTestResult({
                    imap: false,
                    smtp: false,
                    errors: { general: error?.message || 'Connection failed' },
                });
                notify('error', error?.message || 'Connection test failed');
            }
        } finally {
            setIsTesting(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setErrors({});

        try {
            await attachAccount({
                ...formData,
                imap_username: formData.imap_username || formData.email,
                smtp_username: formData.smtp_username || formData.email,
                smtp_password: formData.smtp_password || formData.imap_password,
            });

            notify('success', 'Email account connected successfully!');
            handleClose();
        } catch (error) {
            if (error?.status === 422) {
                setErrors(error.errors || {});
            } else {
                notify('error', error?.message || 'Failed to connect email account');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setStep(1);
        setTestResult(null);
        setErrors({});
        setFormData({
            email: '',
            display_name: '',
            imap_host: '',
            imap_port: 993,
            imap_encryption: 'ssl',
            imap_username: '',
            imap_password: '',
            smtp_host: '',
            smtp_port: 587,
            smtp_encryption: 'tls',
            smtp_username: '',
            smtp_password: '',
            is_default: false,
        });
        onClose();
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="project-composer-overlay" onClick={handleBackdropClick}>
            <div className="project-composer-modal">
                <div className="project-composer-header">
                    <h2 className="project-composer-title">
                        {step === 1 ? t('pages.email.account_setup.step_add') : t('pages.email.account_setup.step_confirm')}
                    </h2>
                    <button
                        className="project-composer-close"
                        onClick={handleClose}
                        aria-label="Close"
                    >
                        <FaTimes size={16} />
                    </button>
                </div>

                <div className="project-composer-body">
                    {step === 1 && (
                        <>
                            <div className="project-form-group">
                                <h4 className="ui-font-14" style={{ fontWeight: 600, marginBottom: '12px' }}>{t('pages.email.account_setup.email_details')}</h4>
                                <div className="project-form-group">
                                    <label className="project-form-label">{t('pages.email.account_setup.email_label')} <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder={t('pages.email.account_setup.email_placeholder')}
                                        className={`project-form-input ${errors.email ? 'is-invalid' : ''}`}
                                    />
                                    {errors.email && <div className="field-error">{errors.email[0]}</div>}
                                </div>
                                <div className="project-form-group">
                                    <label className="project-form-label">{t('pages.email.account_setup.display_name_label')}</label>
                                    <input
                                        type="text"
                                        name="display_name"
                                        value={formData.display_name}
                                        onChange={handleChange}
                                        placeholder={t('pages.email.account_setup.display_name_placeholder')}
                                        className={`project-form-input ${errors.display_name ? 'is-invalid' : ''}`}
                                    />
                                    {errors.display_name && <div className="field-error">{errors.display_name[0]}</div>}
                                </div>
                            </div>

                            <div className="project-form-group">
                                <h4 className="ui-font-14" style={{ fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>{t('pages.email.account_setup.imap_heading')}</h4>
                                <div className="project-form-row">
                                    <div className="project-form-group" style={{ flex: 2 }}>
                                        <label className="project-form-label">{t('pages.email.account_setup.imap_host_label')} <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            name="imap_host"
                                            value={formData.imap_host}
                                            onChange={handleChange}
                                            placeholder={t('pages.email.account_setup.imap_host_placeholder')}
                                            className={`project-form-input ${errors.imap_host ? 'is-invalid' : ''}`}
                                        />
                                        {errors.imap_host && <div className="field-error">{errors.imap_host[0]}</div>}
                                    </div>
                                    <div className="project-form-group" style={{ flex: 1 }}>
                                        <label className="project-form-label">{t('pages.email.account_setup.port_label')}</label>
                                        <input
                                            type="number"
                                            name="imap_port"
                                            value={formData.imap_port}
                                            onChange={handleChange}
                                            className={`project-form-input ${errors.imap_port ? 'is-invalid' : ''}`}
                                        />
                                        {errors.imap_port && <div className="field-error">{errors.imap_port[0]}</div>}
                                    </div>
                                    <div className="project-form-group" style={{ flex: 1 }}>
                                        <label className="project-form-label">{t('pages.email.account_setup.encryption_label')}</label>
                                        <select
                                            name="imap_encryption"
                                            value={formData.imap_encryption}
                                            onChange={handleChange}
                                            className={`project-form-select ${errors.imap_encryption ? 'is-invalid' : ''}`}
                                        >
                                            <option value="ssl">{t('pages.email.account_setup.ssl')}</option>
                                            <option value="tls">{t('pages.email.account_setup.tls')}</option>
                                            <option value="none">{t('pages.email.account_setup.none')}</option>
                                        </select>
                                        {errors.imap_encryption && <div className="field-error">{errors.imap_encryption[0]}</div>}
                                    </div>
                                </div>
                                <div className="project-form-group">
                                    <label className="project-form-label">{t('pages.email.account_setup.imap_user_label')}</label>
                                    <input
                                        type="text"
                                        name="imap_username"
                                        value={formData.imap_username}
                                        onChange={handleChange}
                                        placeholder={t('pages.email.account_setup.imap_user_placeholder')}
                                        className={`project-form-input ${errors.imap_username ? 'is-invalid' : ''}`}
                                    />
                                    {errors.imap_username && <div className="field-error">{errors.imap_username[0]}</div>}
                                </div>
                                <div className="project-form-group">
                                    <label className="project-form-label">{t('pages.email.account_setup.password_label')} <span className="required">*</span></label>
                                    <input
                                        type="password"
                                        name="imap_password"
                                        value={formData.imap_password}
                                        onChange={handleChange}
                                        placeholder={t('pages.email.account_setup.password_placeholder')}
                                        className={`project-form-input ${errors.imap_password ? 'is-invalid' : ''}`}
                                    />
                                    {errors.imap_password && <div className="field-error">{errors.imap_password[0]}</div>}
                                </div>
                            </div>

                            <div className="project-form-group">
                                <h4 className="ui-font-14" style={{ fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>{t('pages.email.account_setup.smtp_heading')}</h4>
                                <div className="project-form-row">
                                    <div className="project-form-group" style={{ flex: 2 }}>
                                        <label className="project-form-label">{t('pages.email.account_setup.smtp_host_label')} <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            name="smtp_host"
                                            value={formData.smtp_host}
                                            onChange={handleChange}
                                            placeholder={t('pages.email.account_setup.smtp_host_placeholder')}
                                            className={`project-form-input ${errors.smtp_host ? 'is-invalid' : ''}`}
                                        />
                                        {errors.smtp_host && <div className="field-error">{errors.smtp_host[0]}</div>}
                                    </div>
                                    <div className="project-form-group" style={{ flex: 1 }}>
                                        <label className="project-form-label">{t('pages.email.account_setup.port_label')}</label>
                                        <input
                                            type="number"
                                            name="smtp_port"
                                            value={formData.smtp_port}
                                            onChange={handleChange}
                                            className={`project-form-input ${errors.smtp_port ? 'is-invalid' : ''}`}
                                        />
                                        {errors.smtp_port && <div className="field-error">{errors.smtp_port[0]}</div>}
                                    </div>
                                    <div className="project-form-group" style={{ flex: 1 }}>
                                        <label className="project-form-label">{t('pages.email.account_setup.encryption_label')}</label>
                                        <select
                                            name="smtp_encryption"
                                            value={formData.smtp_encryption}
                                            onChange={handleChange}
                                            className={`project-form-select ${errors.smtp_encryption ? 'is-invalid' : ''}`}
                                        >
                                            <option value="tls">{t('pages.email.account_setup.tls')}</option>
                                            <option value="ssl">{t('pages.email.account_setup.ssl')}</option>
                                            <option value="none">{t('pages.email.account_setup.none')}</option>
                                        </select>
                                        {errors.smtp_encryption && <div className="field-error">{errors.smtp_encryption[0]}</div>}
                                    </div>
                                </div>
                                <div className="project-form-group">
                                    <label className="project-form-label">{t('pages.email.account_setup.smtp_user_label')}</label>
                                    <input
                                        type="text"
                                        name="smtp_username"
                                        value={formData.smtp_username}
                                        onChange={handleChange}
                                        placeholder={t('pages.email.account_setup.imap_user_placeholder')}
                                        className={`project-form-input ${errors.smtp_username ? 'is-invalid' : ''}`}
                                    />
                                    {errors.smtp_username && <div className="field-error">{errors.smtp_username[0]}</div>}
                                </div>
                                <div className="project-form-group">
                                    <label className="project-form-label">{t('pages.email.account_setup.smtp_pass_label')}</label>
                                    <input
                                        type="password"
                                        name="smtp_password"
                                        value={formData.smtp_password}
                                        onChange={handleChange}
                                        placeholder={t('pages.email.account_setup.smtp_pass_placeholder')}
                                        className={`project-form-input ${errors.smtp_password ? 'is-invalid' : ''}`}
                                    />
                                    {errors.smtp_password && <div className="field-error">{errors.smtp_password[0]}</div>}
                                </div>
                            </div>

                            {testResult && (
                                <div className="test-result" style={{ marginTop: '16px', padding: '12px', background: '#f9f9f9', borderRadius: '6px' }}>
                                    <div className={`test-item ${testResult.imap ? 'success' : 'error'}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: testResult.imap ? '#22c55e' : '#ef4444' }}>
                                        {testResult.imap ? <FaCheckCircle /> : <FaTimesCircle />}
                                        <span className="ui-font-13" >{t('pages.email.account_setup.imap_status')}: {testResult.imap ? t('pages.email.account_setup.connected') : testResult.errors?.imap || 'Failed'}</span>
                                    </div>
                                    <div className={`test-item ${testResult.smtp ? 'success' : 'error'}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: testResult.smtp ? '#22c55e' : '#ef4444' }}>
                                        {testResult.smtp ? <FaCheckCircle /> : <FaTimesCircle />}
                                        <span className="ui-font-13" >{t('pages.email.account_setup.smtp_status')}: {testResult.smtp ? t('pages.email.account_setup.connected') : testResult.errors?.smtp || 'Failed'}</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {step === 2 && (
                        <div className="setup-confirm" style={{ textAlign: 'center', padding: '24px 0' }}>
                            <div className="confirm-icon" style={{ color: '#22c55e', fontSize: '48px', marginBottom: '16px' }}>
                                <FaCheckCircle />
                            </div>
                            <h3 className="ui-font-18" style={{ margin: '0 0 8px' }}>{t('pages.email.account_setup.success_heading')}</h3>
                            <p style={{ color: '#666', margin: '0 0 24px' }}>{t('pages.email.account_setup.success_desc')}</p>

                            <div className="confirm-details ui-font-13" style={{ textAlign: 'left', padding: '16px', background: '#f9f9f9', borderRadius: '6px', marginBottom: '16px' }}>
                                <div style={{ marginBottom: '8px' }}><strong>{t('pages.email.account_setup.email_field')}:</strong> {formData.email}</div>
                                <div style={{ marginBottom: '8px' }}><strong>{t('pages.email.account_setup.imap_field')}:</strong> {formData.imap_host}:{formData.imap_port}</div>
                                <div><strong>{t('pages.email.account_setup.smtp_field')}:</strong> {formData.smtp_host}:{formData.smtp_port}</div>
                            </div>

                            <label className="setup-checkbox ui-font-14" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', justifyContent: 'center' }}>
                                <input
                                    type="checkbox"
                                    name="is_default"
                                    checked={formData.is_default}
                                    onChange={handleChange}
                                />
                                {t('pages.email.account_setup.set_default')}
                            </label>
                        </div>
                    )}
                </div>

                <div className="project-composer-footer">
                    {step === 1 ? (
                        <>
                            <button
                                className="project-composer-btn project-composer-btn-secondary"
                                onClick={handleClose}
                            >
                                {t('pages.email.account_setup.cancel')}
                            </button>
                            <button
                                className="project-composer-btn project-composer-btn-primary"
                                onClick={handleTestConnection}
                                disabled={isTesting}
                            >
                                {isTesting ? (
                                    <>
                                        <FaSpinner className="spin" style={{ marginRight: '8px' }} /> {t('pages.email.account_setup.testing')}
                                    </>
                                ) : (
                                    t('pages.email.account_setup.test_connection')
                                )}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className="project-composer-btn project-composer-btn-secondary"
                                onClick={() => setStep(1)}
                            >
                                {t('pages.email.account_setup.back')}
                            </button>
                            <button
                                className="project-composer-btn project-composer-btn-primary"
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? t('pages.email.account_setup.saving') : t('pages.email.account_setup.add_account')}
                            </button>
                        </>
                    )}
                </div>
            </div>
            <style>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>,
        document.body
    );
};

export default AccountSetupModal;
